/**
 * useVisualKeyboardOffset — track the iOS keyboard's vertical extent.
 *
 * Returns a number: the pixel offset between window.innerHeight and
 * window.visualViewport.height. When the iOS soft keyboard is open
 * this equals the keyboard's height. When closed, returns 0.
 *
 * Why this exists:
 *   Capacitor's WKWebView resizes the viewport when the keyboard
 *   appears, but a flex-column page like Sommelier (header / scrolling
 *   messages / pinned input) doesn't always have the input bar end up
 *   exactly at the new bottom — depending on the parent container's
 *   own height management, the input can end up overlapped by the
 *   keyboard. Applying this offset as a transform / padding on the
 *   pinned input keeps it glued just above the keyboard, matching the
 *   Apple Mail compose pattern.
 *
 * Usage:
 *   const keyboardPx = useVisualKeyboardOffset()
 *   <div style={{ transform: `translateY(-${keyboardPx}px)` }} />
 *
 * Safety:
 *   • Server-side renders and pre-mount returns 0.
 *   • If window.visualViewport is unavailable (older WKWebView, web
 *     fallback) returns 0 — the page just doesn't lift, which is the
 *     baseline behavior.
 *   • The 'scroll' event is also subscribed because iOS fires scroll
 *     (not resize) when the user pulls the keyboard from open → closed
 *     via swipe-down.
 */

import { useEffect, useState } from 'react'

export function useVisualKeyboardOffset() {
  const [offset, setOffset] = useState(0)

  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) return

    const update = () => {
      const diff = window.innerHeight - window.visualViewport.height
      // Clamp negative just in case (some browsers go slightly negative
      // when the viewport overshoots during keyboard animation)
      setOffset(diff > 0 ? diff : 0)
    }

    update()
    window.visualViewport.addEventListener('resize', update)
    window.visualViewport.addEventListener('scroll', update)
    return () => {
      window.visualViewport.removeEventListener('resize', update)
      window.visualViewport.removeEventListener('scroll', update)
    }
  }, [])

  return offset
}
